/** W82 Design-Web-1 (D-1661, DWW-1): der Brief als Gegenstand aus Papier, Einzelmotiv nach `9c93e244:web/src/neu/papierbrief.ts`
 *  (Entwurf) und `d07b42f7:ios/App/Quellen/BuehneEroeffnungAnsicht.swift` (Heldbrief mit Lasche und Bogen). Nur in der Bühne
 *  (Eröffnung, Ablegen-Flug), nie als Fläche der App. Gebaut aus Elementen ohne Inline-Farben: Papier, Logo und Tinte kommen aus
 *  tokens.css (`--brief-*`). Für Vorlesen verborgen. Keine Personendaten: das Fenster trägt Tintenstriche statt einer Anschrift,
 *  Absender sind die Namen, die die Seite selbst zeigt, sonst Gattungsnamen. */
export type Papier = "weiss" | "creme" | "kuehl" | "umwelt";
export type Form = "dl" | "c6" | "c5";
export type Logo = "welle" | "blitz" | "schild" | "kreuz" | "haus" | "schirm";
export type BriefDaten = {
  name: string;
  form: Form;
  papier: Papier;
  logo?: Logo;
  /** Farbe des Logos (Tokens `--brief-logo-a` bis `-f`) */
  farbe?: "a" | "b" | "c" | "d" | "e" | "f";
  /** Rückseite mit Klappen statt Vorderseite */
  hinten?: boolean;
  vermerk?: string;
};
export type HeldDaten = { kopf: string; betreff: string; zeile: string };

export type Brief = {
  el: HTMLDivElement;
  koerper: HTMLDivElement;
  schatten: HTMLDivElement;
  glanz: HTMLDivElement;
  dunkel: HTMLDivElement;
  w: number;
  h: number;
  /** nur am Heldbrief */
  held?: { lascheZu: HTMLElement; lascheAuf: HTMLElement; bogen: HTMLElement; mark: HTMLElement };
};

export function masse(form: Form, breite: number): [number, number] {
  if (form === "dl") return [breite, breite / 2];
  if (form === "c6") return [breite * 0.86, (breite * 0.86) / 1.42];
  return [breite * 0.96, (breite * 0.96) / 1.41];
}

function teil(klasse: string, kinder: (Node | string)[] = [], tag = "div"): HTMLElement {
  const el = document.createElement(tag);
  el.className = klasse;
  for (const k of kinder) el.append(k);
  return el;
}

function vorn(d: BriefDaten): HTMLElement {
  const logo = teil("bb-logo", [], "span");
  logo.dataset.logo = d.logo ?? "welle";
  logo.dataset.farbe = d.farbe ?? "a";
  const name = teil("bb-name", [d.name], "span");
  const kinder: HTMLElement[] = [
    teil("bb-absender", [logo, name]),
    teil("bb-frei"),
    // Fenster: Tintenstriche statt einer Anschrift (keine Personendaten)
    teil("bb-fenster", [teil("bb-zeile bb-kurz", [], "span"), teil("bb-zeile", [], "span"), teil("bb-zeile bb-mittel", [], "span")]),
  ];
  if (d.vermerk) kinder.push(teil("bb-vermerk", [d.vermerk]));
  return teil("bb-seite bb-vorn", kinder);
}

function hinten(): HTMLElement {
  return teil("bb-seite bb-hinten", [teil("bb-klappe"), teil("bb-lasche-flach")]);
}

/** Rückseite des Heldbriefs: Innenseite, offene Lasche (hinter dem Bogen), Bogen mit der markierten Zeile, Tasche, geschlossene
 *  Lasche (vor allem); wie BuehneHeldHinten (iOS). */
function heldRueck(d: BriefDaten, h: HeldDaten) {
  const lascheAuf = teil("bb-lasche bb-lasche-auf");
  const logo = teil("bb-logo", [], "span");
  logo.dataset.logo = d.logo ?? "schirm";
  logo.dataset.farbe = d.farbe ?? "a";
  const mark = teil("bb-mark", [], "span");
  const bogen = teil("bb-bogen", [
    teil("bb-bogen-kopf", [logo, teil("bb-bogen-name", [h.kopf], "span")]),
    teil("bb-bogen-betreff", [h.betreff]),
    teil("bb-bogen-text", ["Guten Tag,"]),
    teil("bb-bogen-beleg", [mark, teil("bb-bogen-zeile", [h.zeile], "span")]),
    teil("bb-strich"),
    teil("bb-strich bb-kurz"),
  ]);
  const lascheZu = teil("bb-lasche bb-lasche-zu");
  const seite = teil("bb-rueck", [teil("bb-innen"), lascheAuf, bogen, teil("bb-tasche"), lascheZu]);
  return { seite, lascheAuf, lascheZu, bogen, mark };
}

/** Baut einen Brief in `ebene`. `breite` in px (DL-Breite); `tiefe` „mitte“ mit Wurfschatten, „fern“ und „nah“ weich. */
export function briefBauen(d: BriefDaten, breite: number, ebene: HTMLElement, tiefe: "mitte" | "fern" | "nah" = "mitte", held?: HeldDaten): Brief {
  const el = document.createElement("div");
  el.className = "bue-brief";
  el.dataset.tiefe = tiefe;
  el.dataset.papier = d.papier;
  el.setAttribute("aria-hidden", "true");
  const [w, h] = masse(d.form, breite);
  const schatten = teil("bue-brief-schatten") as HTMLDivElement;
  schatten.style.width = `${(w * 0.78).toFixed(1)}px`;
  schatten.style.height = `${(h * 0.7).toFixed(1)}px`;
  const koerper = teil("bue-brief-koerper") as HTMLDivElement;
  koerper.style.width = `${w.toFixed(1)}px`;
  koerper.style.height = `${h.toFixed(1)}px`;
  koerper.style.fontSize = `${(w / 100).toFixed(3)}px`;
  const glanz = teil("bue-brief-glanz") as HTMLDivElement;
  const dunkel = teil("bue-brief-dunkel") as HTMLDivElement;
  let teile: Brief["held"];
  if (held) {
    koerper.dataset.held = "";
    const r = heldRueck(d, held);
    const vorne = vorn(d);
    vorne.append(glanz, dunkel);
    koerper.append(vorne, r.seite);
    teile = { lascheZu: r.lascheZu, lascheAuf: r.lascheAuf, bogen: r.bogen, mark: r.mark };
  } else {
    koerper.append(d.hinten ? hinten() : vorn(d), glanz, dunkel);
  }
  el.append(schatten, koerper);
  // abgeleitete Maße einer Miniatur (em nach Briefbreite): vom Raster der Bausteine ausgenommen (abnahme.ts Regel 9)
  for (const e of [el, ...el.querySelectorAll("*")]) e.setAttribute("data-raster-ausnahme", "");
  ebene.append(el);
  return { el, koerper, schatten, glanz, dunkel, w, h, ...(teile ? { held: teile } : {}) };
}

// ---------------------------------------------------------------- Lage im Raum (wie BuehneProjektion, 900 px Abstand)

export const PERSP = 900;
const px = (v: number) => `${v.toFixed(2)}px`;
const grad = (v: number) => `${v.toFixed(2)}deg`;

/** Körper: Mitte (x, y), Neigung rx, ry, Drehung rz, Maßstab s */
export function koerperTf(x: number, y: number, w: number, h: number, rx: number, ry: number, rz: number, s: number): string {
  return `translate3d(${px(x - w / 2)}, ${px(y - h / 2)}, 0) perspective(${PERSP}px) rotateX(${grad(rx)}) rotateY(${grad(ry)}) rotateZ(${grad(rz)}) scale(${s.toFixed(4)})`;
}

/** Wurfschatten im Bildschirmraum: Abstand und Größe zeigen die Höhe */
export function schattenTf(x: number, y: number, w: number, h: number, rz: number, sx: number, sy: number): string {
  return `translate3d(${px(x - w / 2)}, ${px(y - h / 2)}, 0) rotate(${grad(rz)}) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`;
}

// Licht oben links vorn: Helligkeit einer Fläche nach Neigung (die Rückseite zählt wie die Vorderseite), wie Buehne.glanz/dunkel
const LICHT = (() => {
  const [a, b, c] = [-0.45, -0.55, 0.7];
  const l = Math.hypot(a, b, c);
  return [a / l, b / l, c / l] as const;
})();
function hell(rx: number, ry: number): number {
  const a = (rx * Math.PI) / 180;
  const b = (ry * Math.PI) / 180;
  const v = Math.cos(a) * Math.cos(b) < 0 ? -1 : 1;
  return v * (Math.sin(b) * LICHT[0] - Math.sin(a) * Math.cos(b) * LICHT[1] + Math.cos(a) * Math.cos(b) * LICHT[2]);
}
const HELL0 = hell(0, 0);
const klemme = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
export const glanzVon = (rx: number, ry: number) => klemme((hell(rx, ry) - HELL0) * 1.5, 0, 0.42);
export const dunkelVon = (rx: number, ry: number) => klemme((HELL0 - hell(rx, ry)) * 1.25, 0, 0.5);

/** Gattungsabsender je Kachel, wenn die Seite keinen Namen kennt (ohne Personendaten). */
export const MUSTER: Record<"ueberfaellig" | "bald" | "pruefen" | "fixkosten" | "fern", BriefDaten[]> = {
  ueberfaellig: [
    { name: "Stadtwerke", form: "dl", papier: "weiss", logo: "welle", farbe: "a" },
    { name: "Inkasso", form: "c6", papier: "kuehl", logo: "schild", farbe: "e" },
  ],
  bald: [
    { name: "Finanzamt", form: "c5", papier: "umwelt", logo: "schild", farbe: "e" },
    { name: "Krankenkasse", form: "c6", papier: "weiss", logo: "kreuz", farbe: "c" },
  ],
  pruefen: [
    { name: "Versicherung", form: "c5", papier: "weiss", logo: "schirm", farbe: "f" },
    { name: "Bank", form: "dl", papier: "kuehl", logo: "welle", farbe: "b" },
  ],
  fixkosten: [
    { name: "Hausverwaltung", form: "c5", papier: "creme", logo: "haus", farbe: "d" },
    { name: "Mobilfunk", form: "dl", papier: "weiss", logo: "blitz", farbe: "b" },
    { name: "Netzbetreiber", form: "dl", papier: "kuehl", logo: "blitz", farbe: "a" },
  ],
  fern: [
    { name: "Bank", form: "dl", papier: "weiss", logo: "welle", farbe: "b", hinten: true },
    { name: "Verein", form: "c6", papier: "creme", hinten: true },
    { name: "Amt", form: "c5", papier: "umwelt", logo: "schild", farbe: "e" },
    { name: "Post", form: "dl", papier: "kuehl", hinten: true },
    { name: "Praxis", form: "c6", papier: "weiss", logo: "kreuz", farbe: "c" },
    { name: "Versand", form: "dl", papier: "creme", hinten: true },
  ],
};

/** Ein Absendername als Brief: Form, Papier, Logo und Farbe hängen nur am Namen (gleiches Haus, gleicher Brief). */
export function briefAus(name: string | null | undefined, ziel: keyof typeof MUSTER, i: number, vermerk?: string): BriefDaten {
  const muster = MUSTER[ziel];
  const vorlage = muster[i % muster.length] ?? { name: "Post", form: "dl", papier: "weiss" };
  if (!name) return vermerk ? { ...vorlage, vermerk } : vorlage;
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  const formen: Form[] = ["dl", "c5", "c6"];
  const papiere: Papier[] = ["weiss", "kuehl", "weiss", "creme", "umwelt"];
  const logos: Logo[] = ["welle", "schild", "kreuz", "haus", "schirm", "blitz"];
  const farben = ["a", "b", "c", "d", "e", "f"] as const;
  return {
    name,
    form: formen[hash % 3] ?? "dl",
    papier: papiere[(hash >>> 3) % papiere.length] ?? "weiss",
    logo: logos[(hash >>> 6) % logos.length] ?? "welle",
    farbe: farben[(hash >>> 9) % farben.length] ?? "a",
    ...(vermerk ? { vermerk } : {}),
  };
}
